// SinglePharmacy.js
// -----------------------------------------------------------------------------
// A Head-Office-style view scoped to ONE pharmacy. A dropdown at the top selects
// the pharmacy (default: first member). Each module is a full-width card: the
// same label as the Head Office summary card, then the same detail grid you get
// by drilling into Head Office — but for this single pharmacy. Clicking a row
// opens the history popup (fridge / cleaning reuse PopupContent).
// -----------------------------------------------------------------------------
import React, { useEffect, useState, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import PopupContent from "./PopupContent";
import PeriodicTaskEntryDialog from "./PeriodicTaskEntryDialog";
import NearMissPharmacyDialog from "./NearMissPharmacyDialog";
import { CompliancePrintReport } from "./RegulatoryCompliance";
import { useMaskNames, displayName, MaskToggleButton } from "./nameMask";
import "./Popup.css";

const API_BASE = "https://api.pharmacegroup.co.uk";

// Module cards shown on the page, in order. label = same as Head Office.
const CARDS = [
  { key: "fridge",        label: "Fridge monitoring" },
  { key: "cleaning",      label: "Cleaning" },
  { key: "dateChecking",  label: "Date checking" },
  { key: "handover",      label: "Handover diary" },
  { key: "cdRegister",    label: "CD register" },
  { key: "patientSafety", label: "Patient safety" },
  { key: "accidentBook",  label: "Accident book" },
  { key: "visitorLogs",   label: "Visitor logs" }
];

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleDateString("en-GB");
};
const fmtDateTime = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return String(d);
  return dt.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

async function apiGet(path, token) {
  const res = await fetch(`${API_BASE}/api/headoffice/${path}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// Read-only viewing-link fetch: no JWT — the share token in the path is the
// credential (see headoffice.js public-view routes).
async function apiGetPublic(viewToken, path) {
  const res = await fetch(`${API_BASE}/api/headoffice/public-view/${viewToken}/${path}`);
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); if (j?.error) msg = j.error; } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Page search: does a row (any object) contain the query in any primitive
// value? Powers the "locate items that match" box at the top of the page.
// ---------------------------------------------------------------------------
function rowMatches(row, q) {
  if (!q) return true;
  const walk = (v) => {
    if (v == null) return false;
    if (typeof v === "object") return Object.values(v).some(walk);
    return String(v).toLowerCase().includes(q);
  };
  return walk(row);
}
const filterRows = (arr, q) => (!q ? (arr || []) : (arr || []).filter(r => rowMatches(r, q)));

// Per-module search filter: returns { data, count } where data has the same
// shape the CardGrid renderer expects, with non-matching rows removed.
function filterModuleData(key, data, q) {
  if (!q || !data) return { data, count: -1 };
  const out = { ...data };
  let count = 0;
  const take = (k) => { out[k] = filterRows(data[k], q); count += out[k].length; };
  switch (key) {
    case "fridge": case "cleaning": case "dateChecking": take("items"); break;
    case "handover": take("overdue"); take("pending"); take("completedToday"); break;
    case "cdRegister": take("registers"); break;
    case "patientSafety": take("incidents"); break;
    case "accidentBook": take("notYetKnown"); take("reportable"); take("notReportable"); break;
    case "visitorLogs": take("visitors"); break;
    default: return { data, count: -1 };
  }
  return { data: out, count };
}

export default function SinglePharmacy({ token, goTo, viewToken = null }) {
  const masked = useMaskNames();
  const readOnly = !!viewToken;
  const viewerOrg = readOnly ? "" : (localStorage.getItem("organisationName") || "");
  const isAdmin = viewerOrg === "Pharmace all";
  const orgQS = (isAdmin || !viewerOrg) ? "" : `?organisationName=${encodeURIComponent(viewerOrg)}`;

  const [pharmacies, setPharmacies] = useState([]);
  const [selected, setSelected] = useState(null); // { pharmacyId, pharmacyName, dbName }
  const [loadingList, setLoadingList] = useState(true);
  const [metaError, setMetaError] = useState(""); // viewer mode: invalid/revoked link

  // Page search — one box at the top filters every card's rows.
  const [searchRaw, setSearchRaw] = useState("");
  const search = searchRaw.trim().toLowerCase();

  // "Create viewing URL" state.
  const [linkBusy, setLinkBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  // "Email inspector summary" state.
  const [showInspectorModal, setShowInspectorModal] = useState(false);
  const [inspectorEmail, setInspectorEmail] = useState("");
  const [inspectorBusy, setInspectorBusy] = useState(false);
  const [inspectorError, setInspectorError] = useState("");
  const [inspectorResult, setInspectorResult] = useState(null); // { pin, url, expiresAt }

  // Printable Regulatory Compliance report overlay (RegulatoryCompliance.js).
  const [showComplianceReport, setShowComplianceReport] = useState(false);

  // Fridge/cleaning history popup (reuses PopupContent shell).
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  // Generic row-detail popup for the other modules.
  const [rowDetail, setRowDetail] = useState(null); // { title, fields: [[label,value],...] }

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    if (readOnly) {
      // Viewing link: the token pins ONE pharmacy — resolve it and go.
      apiGetPublic(viewToken, "meta")
        .then(meta => {
          if (cancelled) return;
          setSelected({ pharmacyId: meta.pharmacyId, pharmacyName: meta.pharmacyName });
          setLoadingList(false);
        })
        .catch(e => { if (!cancelled) { setMetaError(String(e.message || e)); setLoadingList(false); } });
      return () => { cancelled = true; };
    }
    apiGet(`single/pharmacies${orgQS}`, token)
      .then(list => {
        if (cancelled) return;
        setPharmacies(list || []);
        // Default to first pharmacy in the (alphabetical) members list.
        if (list && list.length) setSelected(list[0]);
        setLoadingList(false);
      })
      .catch(() => { if (!cancelled) setLoadingList(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewToken]);

  // Mint (or re-use) the read-only share link for the selected pharmacy and
  // put it on the clipboard. One stable token per pharmacy (backend re-uses).
  const createViewingUrl = async () => {
    if (!selected?.pharmacyId) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/headoffice/single/view-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pharmacyId: selected.pharmacyId })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const url = `${window.location.origin}${window.location.pathname}?spview=${d.token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); } catch (_) {}
    } catch (e) { window.alert(`Couldn't create the viewing URL: ${e.message || e}`); }
    finally { setLinkBusy(false); }
  };

  // Mint a time-limited (6h), PIN-gated "inspector summary" link for the
  // selected pharmacy and email it to the given address. The PIN comes back
  // in the response for one-time on-screen display — never emailed — so the
  // person here can write it down and hand it to the inspector separately.
  const emailInspectorSummary = async () => {
    if (!selected?.pharmacyId || !inspectorEmail.trim()) return;
    setInspectorBusy(true); setInspectorError("");
    try {
      const res = await fetch(`${API_BASE}/api/inspector-summary/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pharmacyId: selected.pharmacyId, email: inspectorEmail.trim() })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setInspectorResult(d);
    } catch (e) { setInspectorError(String(e.message || e)); }
    finally { setInspectorBusy(false); }
  };


  // Open the fridge/cleaning history popup for the selected pharmacy, using the
  // same per-pharmacy endpoints Head Office uses (POST {pharmacyName}).
  const openHistory = useCallback(async (moduleKey, preloadedItems) => {
    if (!selected) return;

    // Cleaning: the card already holds exactly what the popup shows (area +
    // next due). Build it directly — no name-matching, no extra fetch.
    if (moduleKey === "cleaning") {
      const details = (preloadedItems || []).map(a => ({
        areaName: a.areaName,
        nextDue: a.nextDue ? new Date(a.nextDue).toLocaleDateString("en-GB") : "—"
      }));
      setPopupData({
        column: "Cleaning",
        pharmacyName: selected.pharmacyName,
        cleaningDetails: details,
        isCleaningPopup: true,
        onClose: () => setPopupData(null)
      });
      return;
    }

    // Fridge: needs full log history (not on the card), so fetch by name.
    // The /fridgelogs endpoint is JWT-only, so on a read-only viewing link the
    // fridge rows show current status without the history popup.
    if (readOnly) return;
    setPopupLoading(true);
    try {
      const res = await fetch(`${API_BASE}/fridgelogs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pharmacyName: selected.lookupName || selected.pharmacyName })
      });
      const json = await res.json();
      setPopupData({
        column: "Fridge",
        pharmacyName: selected.pharmacyName,
        fridgeLogs: json.logs || [],
        isFridgePopup: true,
        onClose: () => setPopupData(null)
      });
    } catch (_) {
      setPopupData(null);
    } finally {
      setPopupLoading(false);
    }
  }, [selected, token]);

  return (
    <div style={S.page}>
      <div style={S.headerRow}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22 }}>
            Single pharmacy{readOnly && selected ? ` — ${selected.pharmacyName}` : ""}
          </h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
            {readOnly
              ? "Read-only view shared via a viewing link. Data can be explored but not changed."
              : "Compliance overview for one pharmacy."}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!readOnly && selected?.pharmacyId && (
            <button onClick={() => setShowComplianceReport(true)} style={S.navBtn}
              title="Printable Regulatory Compliance report for this pharmacy — every compliance heading with its data source and supporting evidence, rendered by the same panels as the Regulatory Compliance board. Use Print → Save as PDF.">
              Export Regulatory compliance report
            </button>
          )}
          {!readOnly && selected?.pharmacyId && (
            <button onClick={() => { setShowInspectorModal(true); setInspectorResult(null); setInspectorError(""); setInspectorEmail(""); }}
              style={S.navBtn}
              title="Email a time-limited link to a live compliance activity summary for this pharmacy (previous 12 months) — PIN-protected, expires after 6 hours.">
              Email inspector summary
            </button>
          )}
          {!readOnly && selected?.pharmacyId && (
            <button onClick={createViewingUrl} disabled={linkBusy} style={S.navBtn}
              title="Generate a shareable read-only URL for this pharmacy's dashboard — anyone with the link can view (and drill down where available) but not edit.">
              {linkBusy ? "Creating…" : "Create viewing URL"}
            </button>
          )}
          {!readOnly && <MaskToggleButton />}
        </div>
      </div>

      {/* Freshly minted share link — shown once, already on the clipboard */}
      {shareUrl && !readOnly && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                      background: "#ecfdf5", border: "1px solid #a7f3d0", borderRadius: 6,
                      padding: "8px 12px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ fontWeight: 700, color: "#065f46" }}>Viewing URL (copied to clipboard):</span>
          <input readOnly value={shareUrl} onFocus={e => e.target.select()}
                 style={{ flex: "1 1 320px", fontSize: 12, padding: "4px 8px",
                          border: "1px solid #a7f3d0", borderRadius: 4, background: "#fff" }} />
          <button style={S.navBtn}
                  onClick={() => { try { navigator.clipboard.writeText(shareUrl); } catch (_) {} }}>Copy</button>
          <button style={{ ...S.navBtn, color: "#6b7280" }} onClick={() => setShareUrl("")}>Dismiss</button>
        </div>
      )}

      {metaError && (
        <div style={{ ...S.card, color: "#7f1d1d" }}>
          This viewing link can't be opened: {metaError}
        </div>
      )}

      {/* Pharmacy selector (hidden on a viewing link — the token pins one pharmacy) */}
      {!readOnly && (
        <div style={S.selectorRow}>
          <label style={{ fontSize: 13, fontWeight: 600, marginRight: 8 }}>Pharmacy:</label>
          {loadingList ? (
            <span style={{ color: "#6b7280", fontSize: 13 }}>Loading…</span>
          ) : pharmacies.length === 0 ? (
            <span style={{ color: "#6b7280", fontSize: 13 }}>No pharmacies available.</span>
          ) : (
            <select
              value={selected?.pharmacyId || ""}
              onChange={(e) => {
                const p = pharmacies.find(x => String(x.pharmacyId) === e.target.value);
                setSelected(p || null);
                setShareUrl("");
              }}
              style={S.select}
            >
              {pharmacies.map((p, i) => (
                <option key={p.pharmacyId || `np-${i}`} value={p.pharmacyId || ""} disabled={!p.pharmacyId}>
                  {displayName(p.pharmacyName, masked)}{!p.pharmacyId ? " (unavailable)" : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {/* Page search — filters the rows of every card below */}
      {selected && (selected.pharmacyId || readOnly) && !metaError && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <input
            type="search"
            value={searchRaw}
            onChange={e => setSearchRaw(e.target.value)}
            placeholder="Search this pharmacy — fridges, areas, registers, tasks, alerts, visitors…"
            style={{ flex: 1, fontSize: 14, padding: "8px 12px",
                     border: "1px solid #d1d5db", borderRadius: 6, boxSizing: "border-box" }}
          />
          {search && (
            <button style={S.navBtn} onClick={() => setSearchRaw("")}>Clear</button>
          )}
        </div>
      )}

      {/* Module cards */}
      {selected && (selected.pharmacyId || readOnly) && CARDS.map(card => (
        <ModuleCard
          key={card.key}
          card={card}
          pharmacy={selected}
          token={token}
          viewToken={viewToken}
          search={search}
          masked={masked}
          onOpenHistory={openHistory}
          onRowDetail={setRowDetail}
        />
      ))}

      {/* Periodic tasks — the group dictionary, this pharmacy's values */}
      {selected && (selected.pharmacyId || readOnly) && (
        <PeriodicTasksCard pharmacy={selected} token={token} viewToken={viewToken} search={search} />
      )}

      {/* Head-office parity cards: SOP sign-off, MHRA alerts, RP sign-in,
          near miss (authed only — its endpoint needs an org token), and the
          staff-training placeholder (no live backend yet, mirrors Head Office). */}
      {selected && (selected.pharmacyId || readOnly) && (
        <>
          <SopSignoffCard pharmacy={selected} token={token} viewToken={viewToken} search={search} onRowDetail={setRowDetail} />
          <MhraAlertsSingleCard pharmacy={selected} token={token} viewToken={viewToken} search={search} onRowDetail={setRowDetail} />
          <RpSignInCard pharmacy={selected} token={token} viewToken={viewToken} search={search} onRowDetail={setRowDetail} />
          {!readOnly && <StaffAppraisalsSingleCard pharmacy={selected} token={token} search={search} onRowDetail={setRowDetail} />}
          <NearMissSingleCard pharmacy={selected} token={token} viewToken={viewToken} search={search} onRowDetail={setRowDetail} />
          <StaffTrainingSingleCard search={search} />
        </>
      )}

      {selected && !selected.pharmacyId && !readOnly && (
        <div style={S.card}>
          <div style={{ color: "#7f1d1d" }}>
            This pharmacy can’t be opened yet — its tenant database mapping is missing.
          </div>
        </div>
      )}

      {/* Activity cards — clinic (services), NMS and routes, focused on this
          pharmacy. Their endpoints need an org token, so they're not part of a
          read-only viewing link. */}
      {selected && !readOnly && (
        <>
          <ActivityCard
            label="Clinic activity"
            endpoint="services_analysis"
            listKey="services"
            token={token}
            pharmacyName={selected.pharmacyName}
            kind="services"
            search={search}
          />
          <ActivityCard
            label="NMS activity"
            endpoint="nms_analysis"
            listKey="nms"
            token={token}
            pharmacyName={selected.pharmacyName}
            kind="nms"
            search={search}
            onRowDetail={setRowDetail}
          />
          {!search && (
            <RouteActivityCard
              token={token}
              pharmacyName={selected.pharmacyName}
              goTo={goTo}
            />
          )}
        </>
      )}

      {/* History popup (fridge / cleaning) */}
      {popupLoading && (
        <div className="popup"><div className="popup-content">Loading…</div></div>
      )}
      {popupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>
            <PopupContent popupData={popupData} />
          </div>
        </div>
      )}

      {/* Generic row-detail popup (handover, CD, patient safety, accident, visitors) */}
      {rowDetail && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setRowDetail(null)}>X</button>
            <h3 style={{ marginTop: 0 }}>{rowDetail.title}</h3>
            <table style={S.table}>
              <tbody>
                {rowDetail.fields.map(([k, v], i) => (
                  <tr key={i}>
                    <td style={{ ...S.td, fontWeight: 600, width: 180, verticalAlign: "top" }}>{k}</td>
                    <td style={S.td}>{v == null || v === "" ? "—" : String(v)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Printable Regulatory Compliance report — rendered by the SAME
          components as the Regulatory Compliance board (see
          CompliancePrintReport in RegulatoryCompliance.js). */}
      {showComplianceReport && selected?.pharmacyId && (
        <CompliancePrintReport
          pharmacyName={selected.pharmacyName}
          pharmacyDetails={selected}
          token={token}
          onClose={() => setShowComplianceReport(false)}
        />
      )}

      {/* "Email inspector summary" modal — collects the inspector's email,
          then (on success) shows the PIN once for the manager to write down
          and hand over separately from the emailed link. */}
      {showInspectorModal && selected?.pharmacyId && (
        <InspectorSummaryModal
          pharmacyName={selected.pharmacyName}
          email={inspectorEmail}
          setEmail={setInspectorEmail}
          busy={inspectorBusy}
          error={inspectorError}
          result={inspectorResult}
          onSend={emailInspectorSummary}
          onClose={() => setShowInspectorModal(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// "Email inspector summary" modal.
// -----------------------------------------------------------------------------
function InspectorSummaryModal({ pharmacyName, email, setEmail, busy, error, result, onSend, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
                  display: "flex", alignItems: "center", justifyContent: "center" }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#fff", borderRadius: 10, width: "min(440px, 92vw)", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>Email inspector summary</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{pharmacyName}</div>
          </div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer", color: "#111827", lineHeight: 1 }}>×</button>
        </div>

        {!result ? (
          <>
            <div style={{ fontSize: 13, color: "#374151", margin: "16px 0 8px", lineHeight: 1.5 }}>
              Sends a link to a live compliance activity summary for this pharmacy (cleaning, fridge, date
              checking, SOP reading, feedback, RP sign-in, visitor log, accident book, near miss, medication
              incidents and MHRA — previous 12 months). The link expires in 6 hours and also needs a PIN,
              which you'll be shown here to pass on separately.
            </div>
            <label style={{ fontSize: 12.5, color: "#374151", fontWeight: 600 }}>Inspector's email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="inspector@example.com"
              style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #d1d5db",
                       borderRadius: 6, fontSize: 14, marginTop: 4, marginBottom: 14 }}
              autoFocus
            />
            <button onClick={onSend} disabled={busy || !email.trim()}
              style={{ width: "100%", background: "#4f46e5", color: "#fff", border: 0, padding: "10px 14px",
                       borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? "default" : "pointer",
                       opacity: busy || !email.trim() ? 0.6 : 1 }}>
              {busy ? "Sending…" : "Send"}
            </button>
            {error && (
              <div style={{ fontSize: 13, color: "#991b1b", background: "#fee2e2", border: "1px solid #fecaca",
                            borderRadius: 6, padding: "8px 12px", marginTop: 12 }}>
                {error}
              </div>
            )}
          </>
        ) : (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, color: "#065f46", background: "#ecfdf5", border: "1px solid #a7f3d0",
                          borderRadius: 6, padding: "10px 12px", marginBottom: 16 }}>
              Link emailed to <b>{email}</b>. It expires at{" "}
              <b>{new Date(result.expiresAt).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</b>.
            </div>
            <div style={{ fontSize: 12.5, color: "#6b7280", marginBottom: 6, fontWeight: 600 }}>
              PIN — write this down and give it to the inspector separately (it is not in the email):
            </div>
            <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 8, textAlign: "center",
                          background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
                          padding: "14px 0", color: "#111827", marginBottom: 16 }}>
              {result.pin}
            </div>
            <button onClick={onClose}
              style={{ width: "100%", background: "#374151", color: "#fff", border: 0, padding: "10px 14px",
                       borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// One full-width module card: label + that pharmacy's detail grid.
// -----------------------------------------------------------------------------
function ModuleCard({ card, pharmacy, token, viewToken, search, masked, onOpenHistory, onRowDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null);
    const p = viewToken
      ? apiGetPublic(viewToken, `module/${card.key}`)
      : apiGet(`single/${card.key}?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
    p.then(d => { if (!cancelled) { setData(d); setLoading(false); } })
     .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [card.key, pharmacy.pharmacyId, token, viewToken]);

  // Page search: if the card's label matches, show the whole card; otherwise
  // filter its rows and hide the card entirely when nothing matches.
  const labelMatch = search && card.label.toLowerCase().includes(search);
  const { data: shown, count } = (search && !labelMatch)
    ? filterModuleData(card.key, data, search)
    : { data, count: -1 };
  if (search && !labelMatch && !loading && !error && count === 0) return null;

  return (
    <div style={S.card}>
      <div style={S.cardLabel}>{card.label}</div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : (
        <CardGrid card={card} data={shown} masked={masked} onOpenHistory={onOpenHistory} onRowDetail={onRowDetail} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Per-module grid renderers.
// -----------------------------------------------------------------------------
function CardGrid({ card, data, onOpenHistory, onRowDetail }) {
  if (!data) return <div style={S.muted}>No data.</div>;

  switch (card.key) {
    case "fridge": {
      const items = data.items || [];
      if (!items.length) return <div style={S.muted}>No fridges recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Fridge</th><th style={S.th}>Last reading</th>
            <th style={S.th}>Min °C</th><th style={S.th}>Max °C</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {items.map((f, i) => {
              // red if out of range / recheck overdue; amber if not logged today.
              const bad = f.outOfRange || f.recheckOverdue;
              const stale = !bad && (!f.loggedToday || !f.hasReading);
              const bg = bad ? "#fee2e2" : stale ? "#fef3c7" : "transparent";
              const statusText = bad ? (f.outOfRange ? "Out of range" : "Recheck overdue")
                : stale ? "Not logged today" : "OK";
              return (
                <tr key={i} style={{ background: bg, cursor: "pointer" }} onClick={() => onOpenHistory("fridge")}>
                  <td style={S.td}>{f.fridgeName}</td>
                  <td style={S.td}>{fmtDateTime(f.lastReadingAt)}</td>
                  <td style={S.td}>{f.minTemperature ?? "—"}</td>
                  <td style={S.td}>{f.maxTemperature ?? "—"}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "cleaning":
    case "dateChecking": {
      const items = data.items || [];
      if (!items.length) return <div style={S.muted}>No areas recorded.</div>;
      const isCleaning = card.key === "cleaning";
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Area</th><th style={S.th}>Next due</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {items.map((a, i) => {
              const bg = a.overdue ? "#fee2e2" : a.neverLogged ? "#fef3c7" : "transparent";
              const statusText = a.overdue ? "Overdue" : a.neverLogged ? "Never logged" : "Up to date";
              return (
                <tr key={i}
                    style={{ background: bg, cursor: "pointer" }}
                    onClick={isCleaning
                      ? () => onOpenHistory("cleaning", items)
                      : () => onRowDetail({
                          title: `Date checking — ${a.areaName}`,
                          fields: [
                            ["Area", a.areaName], ["Next due", fmtDate(a.nextDue)],
                            ["Frequency", a.frequency], ["Status", statusText]
                          ]
                        })}>
                  <td style={S.td}>{a.areaName}</td>
                  <td style={S.td}>{fmtDate(a.nextDue)}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "handover": {
      const groups = [
        { key: "overdue", title: "Overdue", bg: "#fee2e2" },
        { key: "pending", title: "Pending", bg: "#fef3c7" },
        { key: "completedToday", title: "Completed today", bg: "#dcfce7" }
      ];
      const any = groups.some(g => (data[g.key] || []).length);
      if (!any) return <div style={S.muted}>Nothing outstanding.</div>;
      return (
        <>
          {groups.map(g => {
            const items = data[g.key] || [];
            if (!items.length) return null;
            return (
              <div key={g.key} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{g.title} ({items.length})</div>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Topic</th><th style={S.th}>For</th>
                    <th style={S.th}>{g.key === "completedToday" ? "Completed" : "Due"}</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{ background: g.bg, cursor: "pointer" }}
                          onClick={() => onRowDetail({
                            title: `Handover — ${it.topic || "item"}`,
                            fields: [
                              ["Topic", it.topic], ["For", it.fao || it.patientName],
                              ["Description", it.description], ["Due", fmtDate(it.dueDate)],
                              ["Completed", it.completedAt ? fmtDateTime(it.completedAt) : null],
                              ["Days overdue", it.daysOverdue]
                            ]
                          })}>
                        <td style={S.td}>{it.topic || "—"}</td>
                        <td style={S.td}>{it.fao || it.patientName || "—"}</td>
                        <td style={S.td}>{fmtDate(g.key === "completedToday" ? it.completedAt : it.dueDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      );
    }

    case "cdRegister": {
      const registers = data.registers || [];
      if (!registers.length) return <div style={S.muted}>No active registers.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Register</th><th style={S.th}>Balance</th>
            <th style={S.th}>Last checked</th><th style={S.th}>Status</th>
          </tr></thead>
          <tbody>
            {registers.map((r, i) => {
              const bad = r.state === "overdue" || r.state === "never";
              const bg = bad ? "#fee2e2" : "transparent";
              const statusText = r.state === "overdue"
                ? `Overdue${r.daysOverdue != null ? ` ${r.daysOverdue}d` : ""}`
                : r.state === "never" ? "Never checked" : "OK";
              return (
                <tr key={i} style={{ background: bg, cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: `CD register — ${r.registerName || "register"}`,
                      fields: [
                        ["Register", r.registerName], ["Category", r.category],
                        ["Current balance", r.currentBalance],
                        ["Last balance check", fmtDate(r.balanceCheckedAt)],
                        ["Status", statusText],
                        ["Expired entries", r.totalExpired]
                      ]
                    })}>
                  <td style={S.td}>{r.registerName || "—"}</td>
                  <td style={S.td}>{r.currentBalance ?? "—"}</td>
                  <td style={S.td}>{fmtDate(r.balanceCheckedAt)}</td>
                  <td style={S.td}>{statusText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "patientSafety": {
      const incidents = data.incidents || [];
      if (!incidents.length) return <div style={S.muted}>No incidents recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Date</th><th style={S.th}>Type</th><th style={S.th}>LFPSE ref</th>
          </tr></thead>
          <tbody>
            {incidents.map((it, i) => {
              const incomplete = it.lfpseSubmitted && !it.lfpseReference;
              return (
                <tr key={i} style={{ background: incomplete ? "#fef3c7" : "transparent", cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: "Patient safety incident",
                      fields: [
                        ["Date", fmtDate(it.incidentDate || it.createdAt)],
                        ["Type", it.incidentType || it.type],
                        ["Description", it.description],
                        ["LFPSE submitted", it.lfpseSubmitted ? "Yes" : "No"],
                        ["LFPSE reference", it.lfpseReference || (incomplete ? "Missing" : null)]
                      ]
                    })}>
                  <td style={S.td}>{fmtDate(it.incidentDate || it.createdAt)}</td>
                  <td style={S.td}>{it.incidentType || it.type || "—"}</td>
                  <td style={S.td}>{it.lfpseReference || (incomplete ? "Missing" : "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    case "accidentBook": {
      // riddorGrid returns { notYetKnown, reportable, notReportable }.
      const groups = [
        { key: "notYetKnown",  title: "RIDDOR status not yet known", bg: "#fee2e2" },
        { key: "reportable",   title: "RIDDOR reportable",           bg: "#fef3c7" },
        { key: "notReportable",title: "Not reportable",              bg: "transparent" }
      ];
      const any = groups.some(g => (data[g.key] || []).length);
      if (!any) return <div style={S.muted}>No accidents recorded.</div>;
      return (
        <>
          {groups.map(g => {
            const items = data[g.key] || [];
            if (!items.length) return null;
            return (
              <div key={g.key} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{g.title} ({items.length})</div>
                <table style={S.table}>
                  <thead><tr>
                    <th style={S.th}>Date</th><th style={S.th}>Manager</th><th style={S.th}>Detail</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i} style={{ background: g.bg, cursor: "pointer" }}
                          onClick={() => onRowDetail({
                            title: "Accident book entry",
                            fields: [
                              ["Date", fmtDate(it.accidentDate || it.date)],
                              ["Manager", it.managerName],
                              ["RIDDOR status", it.riddorStatus],
                              ["RIDDOR reference", it.reference],
                              ["Detail", it.description],
                              ["Days since", it.daysSinceAccident]
                            ]
                          })}>
                        <td style={S.td}>{fmtDate(it.accidentDate || it.date)}</td>
                        <td style={S.td}>{it.managerName || "—"}</td>
                        <td style={S.td}>{it.reference || it.description || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      );
    }

    case "visitorLogs": {
      const visitors = data.visitors || [];
      if (!visitors.length) return <div style={S.muted}>No visitors recorded.</div>;
      return (
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Visitor</th><th style={S.th}>Company</th>
            <th style={S.th}>In</th><th style={S.th}>Out</th>
          </tr></thead>
          <tbody>
            {visitors.map((v, i) => {
              const stale = v.signedInAt && !v.signedOutAt;
              return (
                <tr key={i} style={{ background: stale ? "#fef3c7" : "transparent", cursor: "pointer" }}
                    onClick={() => onRowDetail({
                      title: "Visitor",
                      fields: [
                        ["Visitor", v.visitorName || v.name],
                        ["Company", v.company],
                        ["Reason", v.reason || v.purpose],
                        ["Signed in", fmtDateTime(v.signedInAt)],
                        ["Signed out", v.signedOutAt ? fmtDateTime(v.signedOutAt) : "Still on site"]
                      ]
                    })}>
                  <td style={S.td}>{v.visitorName || v.name || "—"}</td>
                  <td style={S.td}>{v.company || "—"}</td>
                  <td style={S.td}>{fmtDateTime(v.signedInAt)}</td>
                  <td style={S.td}>{v.signedOutAt ? fmtDateTime(v.signedOutAt) : "Still on site"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    }

    default:
      return <div style={S.muted}>—</div>;
  }
}

// -----------------------------------------------------------------------------
// Activity card — clinic (services) or NMS, scoped to one pharmacy. Reuses the
// existing /services_analysis and /nms_analysis endpoints (POST {allowedPharmacies}),
// then filters the response to the selected pharmacy by name.
// -----------------------------------------------------------------------------
function ActivityCard({ label, endpoint, listKey, token, pharmacyName, kind, search = "", onRowDetail }) {
  const [rows, setRows] = useState(null);
  const [raw, setRaw] = useState(null);      // this pharmacy's raw payload (for the chart)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showGraph, setShowGraph] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setRows(null); setRaw(null);

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const organisationName = localStorage.getItem("organisationName") || "";
    const allowedPharmacies = organisationName === "Pharmace all"
      ? ["xxx"]
      : members.map(m => m.pharmacyId);

    fetch(`${API_BASE}/${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ allowedPharmacies })
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        const data = json.data || [];
        const mine = data.find(p => p.pharmacyName === pharmacyName);
        setRaw(mine || null);
        setRows(summarise(mine, kind, listKey));
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [endpoint, listKey, token, pharmacyName, kind]);

  const canGraph = kind === "services" && raw && (raw.services || []).length > 0;

  // Page search: label match shows everything; otherwise filter rows and hide
  // the card when nothing matches.
  const labelMatch = search && label.toLowerCase().includes(search);
  const shownRows = (search && !labelMatch) ? filterRows(rows, search) : rows;
  if (search && !labelMatch && !loading && !error && (!shownRows || shownRows.length === 0)) return null;

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={S.cardLabel}>{label}</div>
        {canGraph && (
          <button onClick={() => setShowGraph(true)} style={S.linkBtn}>View graph →</button>
        )}
      </div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : !shownRows || shownRows.length === 0 ? (
        <div style={S.muted}>No activity recorded.</div>
      ) : (
        <table style={{ ...S.table, cursor: canGraph ? "pointer" : "default" }}
               onClick={canGraph ? () => setShowGraph(true) : undefined}>
          <thead><tr>
            <th style={S.th}>{kind === "nms" ? "Stage" : "Service"}</th>
            <th style={S.th}>Completed</th>
            <th style={S.th}>{kind === "nms" ? "In progress" : "Pending"}</th>
            {kind === "services" && <th style={S.th}>Cancelled</th>}
          </tr></thead>
          <tbody>
            {shownRows.map((r, i) => (
              <tr key={i}
                  style={onRowDetail ? { cursor: "pointer" } : undefined}
                  onClick={onRowDetail ? (e) => { e.stopPropagation(); onRowDetail({
                    title: `${label} — ${r.name}`,
                    fields: [
                      [kind === "nms" ? "Stage" : "Service", r.name],
                      ["Completed", r.completed],
                      [kind === "nms" ? "In progress" : "Pending", r.middle],
                      ...(kind === "services" ? [["Cancelled", r.cancelled]] : [])
                    ]
                  }); } : undefined}>
                <td style={S.td}>{r.name}</td>
                <td style={S.td}>{r.completed}</td>
                <td style={S.td}>{r.middle}</td>
                {kind === "services" && <td style={S.td}>{r.cancelled}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showGraph && canGraph && (
        <ClinicGraphPopup pharm={raw} pharmacyName={pharmacyName} onClose={() => setShowGraph(false)} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Route activity mini-widget — recent delivery rounds for this pharmacy.
// Reuses /routeanalysis_list (POST {pharmacyIds:[dbName], page, limit}).
// Resolves the pharmacy's dbName from localStorage members by name (same source
// the full Route analysis page uses).
// -----------------------------------------------------------------------------
function RouteActivityCard({ token, pharmacyName, goTo }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setItems(null);

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const mem = members.find(m => m.pharmacyName === pharmacyName);
    const dbName = mem?.pharmacyId ? String(mem.pharmacyId) : null;
    if (!dbName) { setItems([]); setLoading(false); return () => {}; }

    fetch(`${API_BASE}/routeanalysis_list`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ pharmacyIds: [dbName], month: null, year: null, page: 1, limit: 10 })
    })
      .then(r => r.json())
      .then(json => {
        if (cancelled) return;
        setItems(Array.isArray(json?.items) ? json.items : []);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token, pharmacyName]);

  // Clicking a round opens the FULL Route analysis page, pre-filtered to this
  // pharmacy (the page reloads its own detail panel, map and all).
  const openInRoutePage = () => {
    if (goTo) goTo("route_analysis", { pharmacyName });
  };

  // Per-round helpers (mirror the main Route page).
  const succ = (it) => Number(it?.results?.deliveries?.successCount || 0);
  const fail = (it) => Number(it?.results?.deliveries?.failCount || 0);
  const deliveries = (it) => succ(it) + fail(it);
  const seconds = (it) => Number(it?.results?.aggregates?.totalActualSecondsForRoute || 0);
  const distanceKm = (it) => {
    const a = it?.results?.aggregates || {};
    const v = a.totalPredictedDistanceKm ?? a.totalOsrmDistanceKm ?? a.totalActualDistanceKm;
    return Number(v || 0) / 1000;   // values are stored in metres
  };
  const secondsToHM = (s) => {
    s = Math.max(0, Math.floor(Number(s) || 0));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
    return h ? `${h}h ${m}m` : `${m}m`;
  };

  // Totals across the listed rounds.
  const totals = (items || []).reduce((acc, it) => {
    acc.deliveries += deliveries(it);
    acc.failed += fail(it);
    acc.seconds += seconds(it);
    acc.km += distanceKm(it);
    return acc;
  }, { deliveries: 0, failed: 0, seconds: 0, km: 0 });

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={S.cardLabel}>Route activity</div>
        {items && items.length > 0 && goTo && (
          <button onClick={openInRoutePage} style={S.linkBtn}>Open full route analysis →</button>
        )}
      </div>
      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : !items || items.length === 0 ? (
        <div style={S.muted}>No recent delivery rounds.</div>
      ) : (
        <>
          {/* Totals across the recent rounds shown below */}
          <div style={S.statRow}>
            <Stat label="Total deliveries" value={totals.deliveries} />
            <Stat label="Failed deliveries" value={totals.failed} highlight={totals.failed > 0} />
            <Stat label="Total time" value={secondsToHM(totals.seconds)} />
            <Stat label="Total distance" value={`${totals.km ? totals.km.toFixed(1) : 0} km`} />
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>
            Totals cover the {items.length} most recent round{items.length === 1 ? "" : "s"} shown.
          </div>

          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Date</th><th style={S.th}>Driver</th>
              <th style={S.th}>Deliveries</th><th style={S.th}>Failed</th>
              <th style={S.th}>Time</th><th style={S.th}>Distance</th><th style={S.th}>Efficiency</th>
            </tr></thead>
            <tbody>
              {items.map((it, i) => {
                const eff = it?.results?.aggregates?.efficiencyActualVsPredicted;
                const km = distanceKm(it);
                return (
                  <tr key={i} style={{ cursor: "pointer" }}
                      title="Open the full route detail in Route analysis"
                      onClick={openInRoutePage}>
                    <td style={S.td}>{fmtDate(it.deliveryDate)}</td>
                    <td style={S.td}>{it.driverName || "—"}</td>
                    <td style={S.td}>{deliveries(it)}</td>
                    <td style={{ ...S.td, color: fail(it) > 0 ? "#b91c1c" : "inherit" }}>{fail(it)}</td>
                    <td style={S.td}>{seconds(it) ? secondsToHM(seconds(it)) : "—"}</td>
                    <td style={S.td}>{km ? `${km.toFixed(1)} km` : "—"}</td>
                    <td style={S.td}>{eff != null ? eff : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// Small stat tile for the route totals row.
function Stat({ label, value, highlight }) {
  return (
    <div style={S.stat}>
      <div style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: highlight ? "#b91c1c" : "#111827" }}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Clinic graph popup — mirrors the Services page chart, locked to one pharmacy.
// Service-selectable + group-by selector + stacked Completed/Cancelled bars.
// -----------------------------------------------------------------------------
function ClinicGraphPopup({ pharm, pharmacyName, onClose }) {
  const [selectedService, setSelectedService] = useState("ALL");
  const [groupMode, setGroupMode] = useState("week");

  const services = useMemo(() => {
    const set = new Set();
    (pharm?.services || []).forEach(s => { if (s.serviceName) set.add(s.serviceName); });
    return Array.from(set).sort();
  }, [pharm]);

  // Build {date -> {completed, cancelled}} for the selected service/group.
  const graphData = useMemo(() => {
    const filtered = [];
    (pharm?.services || []).forEach(s => {
      if (selectedService !== "ALL" &&
          s.serviceName.toLowerCase().trim() !== selectedService.toLowerCase().trim()) return;
      (s.dailyStats || []).forEach(d => filtered.push({ date: d.date, stats: d.stats }));
    });

    const getKey = (dateStr) => {
      if (!dateStr) return "—";
      const [d, m, y] = String(dateStr).split("/");
      const dt = new Date(`${y}-${m}-${d}`);
      if (groupMode === "day") return dateStr;
      if (groupMode === "week") return `${m}/${y} W${Math.ceil((dt.getDate() || 1) / 7)}`;
      if (groupMode === "month") return `${m}/${y}`;
      if (groupMode === "year") return `${y}`;
      return dateStr;
    };

    const grouped = {};
    filtered.forEach(item => {
      const key = getKey(item.date);
      if (!grouped[key]) grouped[key] = { completed: 0, cancelled: 0 };
      (item.stats || []).forEach(st => {
        if (st.status === "COMPLETED") grouped[key].completed += st.count || 0;
        if (st.status === "CANCELLED") grouped[key].cancelled += st.count || 0;
      });
    });
    return Object.entries(grouped).map(([date, v]) => ({ date, ...v }));
  }, [pharm, selectedService, groupMode]);

  const xAxis = graphData.map(d => d.date);
  const chartOption = {
    tooltip: { trigger: "axis" },
    legend: { top: 0, data: ["Completed", "Cancelled"] },
    grid: { top: 50, left: 40, right: 20, bottom: 60 },
    xAxis: { type: "category", data: xAxis, axisLabel: { rotate: 45 } },
    yAxis: { type: "value" },
    series: [
      { name: "Completed", type: "bar", stack: "total", data: graphData.map(d => d.completed), itemStyle: { color: "green" } },
      { name: "Cancelled", type: "bar", stack: "total", data: graphData.map(d => d.cancelled), itemStyle: { color: "red" } }
    ]
  };

  return (
    <div className="popup" onClick={onClose}>
      <div className="popup-content" onClick={e => e.stopPropagation()} style={{ width: "min(900px, 95vw)" }}>
        <button className="close-btn" onClick={onClose}>X</button>
        <h3 style={{ marginTop: 0 }}>Clinic activity — {pharmacyName}</h3>

        <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
          <select value={selectedService} onChange={e => setSelectedService(e.target.value)}
                  style={S.select}>
            <option value="ALL">All services</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={groupMode} onChange={e => setGroupMode(e.target.value)} style={S.select}>
            <option value="day">By day</option>
            <option value="week">By week</option>
            <option value="month">By month</option>
            <option value="year">By year</option>
          </select>
        </div>

        {graphData.length === 0 ? (
          <div style={S.muted}>No data for this selection.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ width: Math.max(700, xAxis.length * 70), height: 380 }}>
              <ReactECharts
                key={`${selectedService}-${groupMode}`}
                option={chartOption}
                style={{ height: "100%", width: "100%" }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Reduce one pharmacy's services/nms payload into compact per-item rows.
function summarise(pharm, kind, listKey) {
  if (!pharm) return [];
  const list = pharm[listKey] || [];

  if (kind === "services") {
    return list.map(s => {
      let completed = 0, cancelled = 0;
      (s.dailyStats || []).forEach(d => (d.stats || []).forEach(st => {
        if (st.status === "COMPLETED") completed += st.count || 0;
        if (st.status === "CANCELLED") cancelled += st.count || 0;
      }));
      return { name: s.serviceName || "—", completed, middle: s.pendingCount || 0, cancelled };
    }).filter(r => r.completed || r.middle || r.cancelled);
  }

  // NMS: group by type across breakdown counts, keyed by service/stage name.
  const byName = {};
  list.forEach(group => {
    const name = group.serviceName || group.name || group.type || "NMS";
    if (!byName[name]) byName[name] = { name, completed: 0, middle: 0 };
    (group.breakdown || []).forEach(b => {
      if (group.type === "COMPLETED") byName[name].completed += b.count || 0;
      if (group.type === "IN_PROGRESS") byName[name].middle += b.count || 0;
    });
  });
  return Object.values(byName).filter(r => r.completed || r.middle);
}

// -----------------------------------------------------------------------------
// Periodic tasks — scoped to ONE pharmacy, using the group-wide Periodic Tasks
// system (periodic_task_defs / entries). Rewired 2026-06 off the legacy
// periodic-entries collection. Lists every active task definition, shows this
// pharmacy's latest recorded value + due-state, and lets staff record a new
// value per task.
//
// Backend:
//   GET  /api/headoffice/periodic-tasks/grid   → { tasks[], rows[] }
//        each row = { pharmacyId, pharmacyName, dbName, cells{ taskId: {value, recordedAt, state} } }
//   POST /api/headoffice/periodic-tasks/entries  { taskId, dbName, pharmacyId, value }
//   POST /api/headoffice/periodic-tasks/optout   { taskId, dbName, pharmacyId, optedOut }
// -----------------------------------------------------------------------------

// Due-state → colour + label. States from the backend grid: in-date, due-soon,
// overdue, never, na (opted out).
// Due-state → colour + label. States as EMITTED BY THE BACKEND grid:
// ok / due / overdue / never / na. (BUG FIX 2026-07: this table previously
// used the keys "in-date" and "due-soon", which the backend never sends —
// every recorded cell missed the lookup and fell through to the "never"
// fallback, so tasks WITH a recording were badged "Never recorded". The old
// keys are kept as aliases in case any cached/legacy payload still uses them.)
const PT_STATE = {
  "ok":       { bg: "#dcfce7", fg: "#166534", label: "In date" },
  "due":      { bg: "#fef9c3", fg: "#854d0e", label: "Due soon" },
  "overdue":  { bg: "#fee2e2", fg: "#991b1b", label: "Overdue" },
  "never":    { bg: "#f3f4f6", fg: "#6b7280", label: "Never recorded" },
  "na":       { bg: "#eef2ff", fg: "#3730a3", label: "Not relevant" },
  // legacy aliases
  "in-date":  { bg: "#dcfce7", fg: "#166534", label: "In date" },
  "due-soon": { bg: "#fef9c3", fg: "#854d0e", label: "Due soon" },
};

function ptDisplayValue(dataType, value) {
  if (value == null || value === "") return "—";
  if (dataType === "boolean") return (value === true || value === "true" || value === "yes" || value === 1) ? "Yes" : "No";
  if (dataType === "date") { const d = new Date(value); return isNaN(d) ? String(value) : d.toLocaleDateString("en-GB"); }
  return String(value);
}

function PeriodicTasksCard({ pharmacy, token, viewToken = null, search = "" }) {
  const readOnly = !!viewToken;
  const [tasks, setTasks] = useState([]);
  const [row, setRow] = useState(null);       // this pharmacy's grid row
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [entryTask, setEntryTask] = useState(null); // task being recorded → unified dialog
  const [history, setHistory] = useState(null);     // { task, entries } → drill popup

  // Drill-down: full entry history for one task at this pharmacy.
  const openHistory = async (task) => {
    try {
      const h = readOnly
        ? await apiGetPublic(viewToken, `periodic-task-history?taskId=${encodeURIComponent(String(task.taskId))}`)
        : await apiGet(`periodic-tasks/history?taskId=${encodeURIComponent(String(task.taskId))}&dbName=${encodeURIComponent(row?.dbName || "")}`, token);
      setHistory({ task, entries: h.entries || [] });
    } catch (e) { window.alert("Couldn't load history: " + (e.message || e)); }
  };

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      if (readOnly) {
        // Viewing link: one-pharmacy slice, no fleet scan, no JWT.
        const g = await apiGetPublic(viewToken, "periodic-tasks");
        setTasks(g?.tasks || []);
        setRow(g?.row || null);
      } else {
        const g = await apiGet("periodic-tasks/grid", token);
        setTasks(g?.tasks || []);
        const mine = (g?.rows || []).find(
          r => String(r.pharmacyId) === String(pharmacy.pharmacyId)
        ) || null;
        setRow(mine);
      }
    } catch (e) { setError(String(e.message || e)); }
    finally { setLoading(false); }
  }, [pharmacy.pharmacyId, token, viewToken, readOnly]);

  useEffect(() => { load(); }, [load]);

  // Toggle "not relevant" for one task at this pharmacy.
  const toggleOptout = async (task, optedOut) => {
    if (!row?.dbName) return;
    try {
      await fetch(`${API_BASE}/api/headoffice/periodic-tasks/optout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ taskId: task.taskId, dbName: row.dbName, pharmacyId: pharmacy.pharmacyId, optedOut }),
      });
      await load();
    } catch (e) { window.alert("Couldn't update: " + (e.message || e)); }
  };

  // Page search: filter tasks by description / frequency / latest value /
  // status label; hide the card when nothing matches (unless its own label does).
  const labelMatch = search && "periodic tasks".includes(search);
  const visibleTasks = (search && !labelMatch)
    ? tasks.filter(task => {
        const cell = row?.cells?.[String(task.taskId)] || { state: "never", value: null };
        const st = PT_STATE[cell.state] || PT_STATE.never;
        return rowMatches({
          description: task.description, frequency: task.frequency,
          value: ptDisplayValue(task.dataType, cell.value), status: st.label,
          note: cell.note
        }, search);
      })
    : tasks;
  if (search && !labelMatch && !loading && !error && visibleTasks.length === 0) return null;

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={S.cardLabel}>Periodic tasks</div>
        <button onClick={load} style={S.navBtn}>Refresh</button>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
        Group-defined recurring tasks. Showing this pharmacy's latest value and status for each.
      </div>

      {loading ? (
        <div style={S.muted}>Loading…</div>
      ) : error ? (
        <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
      ) : tasks.length === 0 ? (
        <div style={S.muted}>No periodic tasks defined for this group yet.</div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Task</th>
              <th style={S.th}>Frequency</th>
              <th style={S.th}>Latest value</th>
              <th style={S.th}>Status</th>
              {!readOnly && <th style={S.th}>Record</th>}
            </tr>
          </thead>
          <tbody>
            {visibleTasks.map(task => {
              const cell = row?.cells?.[String(task.taskId)] || { state: "never", value: null };
              const st = PT_STATE[cell.state] || PT_STATE.never;
              const isNa = cell.state === "na";
              return (
                <tr key={String(task.taskId)}>
                  <td style={{ ...S.td, cursor: "pointer" }}
                      title="Click to view this task's full entry history at this pharmacy"
                      onClick={() => openHistory(task)}>
                    <div style={{ fontWeight: 600 }}>{task.description}</div>
                    {cell.recordedAt && (
                      <div style={{ fontSize: 11, color: "#9ca3af" }}>
                        Last: {new Date(cell.recordedAt).toLocaleDateString("en-GB")}
                      </div>
                    )}
                    {cell.note && (
                      <div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic" }} title={cell.note}>
                        “{cell.note.length > 90 ? cell.note.slice(0, 90) + "…" : cell.note}”
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, textTransform: "capitalize" }}>{task.frequency}</td>
                  <td style={S.td}>{isNa ? "—" : ptDisplayValue(task.dataType, cell.value)}</td>
                  <td style={S.td}>
                    <span style={{ background: st.bg, color: st.fg, fontSize: 11, fontWeight: 700,
                                   padding: "2px 8px", borderRadius: 999 }}>{st.label}</span>
                  </td>
                  {!readOnly && (
                    <td style={S.td}>
                      {isNa ? (
                        <button onClick={() => toggleOptout(task, false)} style={{ ...S.linkBtn, padding: "2px 8px" }}>
                          Mark relevant
                        </button>
                      ) : (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <button onClick={() => setEntryTask({ task, cell })}
                                  style={{ ...S.linkBtn, padding: "2px 10px" }}>
                            Record…
                          </button>
                          <button
                            onClick={() => toggleOptout(task, true)}
                            title="Mark this task as not relevant for this pharmacy"
                            style={{ ...S.linkBtn, padding: "2px 6px", color: "#6b7280",
                                     borderColor: "#e5e7eb", fontSize: 11 }}>
                            N/A
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Drill-down: entry history for one task */}
      {history && (
        <div className="popup" onClick={() => setHistory(null)}>
          <div className="popup-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <button className="close-btn" onClick={() => setHistory(null)}>X</button>
            <h3 style={{ marginTop: 0 }}>{history.task.description}</h3>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10 }}>
              Every recorded value at {pharmacy.pharmacyName}, newest first.
            </div>
            {history.entries.length === 0 ? (
              <div style={S.muted}>No entries recorded yet.</div>
            ) : (
              <table style={S.table}>
                <thead><tr>
                  <th style={S.th}>Recorded</th><th style={S.th}>Value</th>
                  <th style={S.th}>Month</th><th style={S.th}>Note</th>
                </tr></thead>
                <tbody>
                  {history.entries.map((e, i) => (
                    <tr key={i}>
                      <td style={S.td}>{fmtDate(e.recordedAt)}</td>
                      <td style={S.td}>{ptDisplayValue(history.task.dataType, e.value)}</td>
                      <td style={S.td}>{e.periodKey || "—"}</td>
                      <td style={S.td}>{e.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Unified record-a-value popup — the same dialog Regulatory Compliance
          and the Head Office grid use. */}
      {entryTask && !readOnly && (
        <PeriodicTaskEntryDialog
          token={token}
          task={entryTask.task}
          target={{ dbName: row?.dbName, pharmacyId: pharmacy.pharmacyId, pharmacyName: pharmacy.pharmacyName }}
          existingEntry={entryTask.cell?.entryId ? {
            entryId: entryTask.cell.entryId, value: entryTask.cell.value,
            recordedAt: entryTask.cell.recordedAt, note: entryTask.cell.note,
            recordedBy: entryTask.cell.recordedBy } : null}
          onClose={() => setEntryTask(null)}
          onSaved={() => { setEntryTask(null); load(); }}
        />
      )}
    </div>
  );
}

const S = {
  page: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  selectorRow: { display: "flex", alignItems: "center", marginBottom: 20, padding: "12px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 },
  select: { fontSize: 14, padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 4, minWidth: 260 },
  // Cards cap their height and scroll internally (2026-07: reduces the page's
  // vertical footprint — long grids no longer stretch the whole page).
  card: { width: "100%", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 18px", marginBottom: 16, boxSizing: "border-box", maxHeight: 340, overflowY: "auto" },
  cardLabel: { fontSize: 16, fontWeight: 600, marginBottom: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "2px solid #e5e7eb", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  td: { padding: "6px 8px", borderBottom: "1px solid #f3f4f6" },
  muted: { fontSize: 13, color: "#9ca3af", fontStyle: "italic" },
  linkBtn: { fontSize: 12, padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", color: "#2D5A3D", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" },
  statRow: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 },
  stat: { flex: "1 1 120px", minWidth: 110, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 12px" },
  navBtn: { fontSize: 12, padding: "5px 12px", border: "1px solid #d1d5db", borderRadius: 4, background: "#fff", color: "#374151", cursor: "pointer" },
  cellInput: { width: "100%", fontSize: 13, border: "1px solid #e5e7eb", borderRadius: 4, padding: "4px 6px", boxSizing: "border-box" }
};
// =============================================================================
// Head-office parity cards (added 2026-07): the panels the Head Office
// dashboard has that this page previously lacked — SOP sign-off, MHRA alerts,
// RP sign-in, near miss, and the staff-training placeholder.
// Each accepts { pharmacy, token, viewToken, search } and follows the same
// search rules as the module cards: label match shows everything, otherwise
// rows are filtered and the card hides when nothing matches.
// =============================================================================

// ---- SOP sign-off — read & signed status for this pharmacy ------------------
function SopSignoffCard({ pharmacy, token, viewToken, search, onRowDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);       // assignment rows (drill)
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null); setDetail(null);
    const p = viewToken
      ? apiGetPublic(viewToken, "sop")
      : apiGet(`single-extras/sop?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
    p.then(d => { if (!cancelled) { setData(d); setLoading(false); } })
     .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [pharmacy.pharmacyId, token, viewToken]);

  // Drill-down: per-assignment rows (staff × SOP), fetched on demand.
  const toggleDetail = async () => {
    if (detail) { setDetail(null); return; }
    setDetailLoading(true);
    try {
      const d = viewToken
        ? await apiGetPublic(viewToken, "sop-detail")
        : await apiGet(`single-extras/sop-detail?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
      setDetail(d.rows || []);
    } catch (e) { window.alert("Couldn't load assignments: " + (e.message || e)); }
    finally { setDetailLoading(false); }
  };

  const labelMatch = search && "sop sign-off sops policies".includes(search);
  if (search && !labelMatch) {
    const hay = { signed: data?.signed, overdue: data?.overdue, pending: data?.pending,
                  status: data?.status, rows: detail };
    if (!loading && !error && !rowMatches(hay, search)) return null;
  }

  const statusBadge = (st) => {
    const m = { ok: ["#dcfce7", "#166534", "All signed / in date"],
                unchecked: ["#fef9c3", "#854d0e", "Signatures pending"],
                fail: ["#fee2e2", "#991b1b", "Signatures overdue"] }[st] || ["#f3f4f6", "#6b7280", st || "—"];
    return <span style={{ background: m[0], color: m[1], fontSize: 11, fontWeight: 700,
                          padding: "2px 8px", borderRadius: 999 }}>{m[2]}</span>;
  };
  const rowBadge = (st) => ({ signed: ["#dcfce7", "#166534", "Signed"],
                              pending: ["#fef9c3", "#854d0e", "Pending"],
                              overdue: ["#fee2e2", "#991b1b", "Overdue"] }[st] || ["#f3f4f6", "#6b7280", st]);

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={S.cardLabel}>SOP sign-off</div>
        {!loading && !error && data?.inUse && (
          <button style={S.linkBtn} onClick={toggleDetail} disabled={detailLoading}>
            {detailLoading ? "Loading…" : detail ? "Hide assignments" : "Show assignments →"}
          </button>
        )}
      </div>
      {loading ? <div style={S.muted}>Loading…</div>
       : error ? <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn't load: {error}</div>
       : !data?.inUse ? <div style={S.muted}>SOP assignments not in use at this pharmacy.</div>
       : (
        <>
          <div style={{ ...S.statRow, cursor: "pointer" }} onClick={toggleDetail}
               title="Click for the per-assignment breakdown">
            <Stat label="Assignments" value={data.total} />
            <Stat label="Signed" value={data.signed} />
            <Stat label="Pending (not due)" value={data.pending} />
            <Stat label="Overdue" value={data.overdue} highlight={data.overdue > 0} />
            <div style={{ ...S.stat, display: "flex", alignItems: "center" }}>{statusBadge(data.status)}</div>
          </div>
          {detail && (
            detail.length === 0 ? <div style={S.muted}>No live assignments.</div> : (
              <table style={{ ...S.table, marginTop: 8 }}>
                <thead><tr>
                  <th style={S.th}>Staff</th><th style={S.th}>SOP</th>
                  <th style={S.th}>Due</th><th style={S.th}>Status</th>
                </tr></thead>
                <tbody>
                  {(search ? filterRows(detail, search) : detail).map((r, i) => {
                    const b = rowBadge(r.status);
                    return (
                      <tr key={i} style={{ cursor: "pointer" }}
                          onClick={() => onRowDetail && onRowDetail({
                            title: `SOP assignment — ${r.sopTitle}`,
                            fields: [
                              ["Staff", r.staffName], ["SOP", r.sopTitle],
                              ["Assigned", fmtDate(r.assignedAt)], ["Due", fmtDate(r.dueDate)],
                              ["Read & signed", r.signedAt ? fmtDateTime(r.signedAt) : "Not yet"],
                              ["Status", b[2]]
                            ]
                          })}>
                        <td style={S.td}>{r.staffName}</td>
                        <td style={S.td}>{r.sopTitle}</td>
                        <td style={S.td}>{fmtDate(r.dueDate)}</td>
                        <td style={S.td}>
                          <span style={{ background: b[0], color: b[1], fontSize: 11, fontWeight: 700,
                                         padding: "2px 8px", borderRadius: 999 }}>{b[2]}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )
          )}
        </>
      )}
    </div>
  );
}

// ---- MHRA alerts — every live alert with this pharmacy's action state -------
function MhraAlertsSingleCard({ pharmacy, token, viewToken, search, onRowDetail }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setRows(null);
    const p = viewToken
      ? apiGetPublic(viewToken, "mhra")
      : apiGet(`single-extras/mhra?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
    p.then(d => { if (!cancelled) { setRows(d.rows || []); setLoading(false); } })
     .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [pharmacy.pharmacyId, token, viewToken]);

  const labelMatch = search && "mhra alerts safety".includes(search);
  const all = rows || [];
  const filtered = (search && !labelMatch) ? filterRows(all, search) : all;
  if (search && !labelMatch && !loading && !error && filtered.length === 0) return null;

  const outstanding = filtered.filter(r => !r.actioned);
  const actioned = filtered.filter(r => r.actioned);
  const shown = showAll ? filtered : [...outstanding, ...actioned.slice(0, 5)];

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={S.cardLabel}>MHRA alerts</div>
        {!loading && !error && all.length > 0 && (
          <span style={{ fontSize: 12, color: outstanding.length ? "#991b1b" : "#166534", fontWeight: 700 }}>
            {outstanding.length ? `${outstanding.length} not yet actioned` : "All live alerts actioned"}
          </span>
        )}
      </div>
      {loading ? <div style={S.muted}>Loading…</div>
       : error ? <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
       : all.length === 0 ? <div style={S.muted}>No live MHRA alerts.</div>
       : (
        <>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Alert</th><th style={S.th}>Class</th>
              <th style={S.th}>Date</th><th style={S.th}>Status</th>
            </tr></thead>
            <tbody>
              {shown.map((a, i) => (
                <tr key={a.alertId || i}
                    style={{ background: a.actioned ? "transparent" : "#fee2e2", cursor: "pointer" }}
                    onClick={() => onRowDetail && onRowDetail({
                      title: `MHRA alert — ${a.title}`,
                      fields: [
                        ["Alert", a.title],
                        ["Class / category", a.classLevel != null ? `Class ${a.classLevel}` : (a.category || "—")],
                        ["Alert date", fmtDate(a.alertDate)],
                        ["Summary", a.summary],
                        ["Actioned here", a.actioned ? `Yes — ${fmtDate(a.actionedAt)}` : "No"],
                        ["Stock affected", a.actioned ? (a.isAffected ? "Yes" : "No") : null],
                        ["Affected stock detail", a.affectedStockDetails],
                        ["Action taken", a.actionTakenByStaff],
                        ["Not affected because", a.notAffectedReason],
                        ["MHRA article", a.articleUrl]
                      ]
                    })}>
                  <td style={S.td}>
                    {a.articleUrl
                      ? <a href={a.articleUrl} target="_blank" rel="noreferrer"
                           style={{ color: "#1d4ed8" }}
                           onClick={e => e.stopPropagation()}>{a.title}</a>
                      : a.title}
                  </td>
                  <td style={S.td}>{a.classLevel != null ? `Class ${a.classLevel}` : (a.category || "—")}</td>
                  <td style={S.td}>{fmtDate(a.alertDate)}</td>
                  <td style={S.td}>
                    {a.actioned
                      ? `Actioned ${a.actionedAt ? fmtDate(a.actionedAt) : ""}`.trim()
                      : "Not actioned"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!showAll && filtered.length > shown.length && (
            <button style={{ ...S.linkBtn, marginTop: 8 }} onClick={() => setShowAll(true)}>
              Show all {filtered.length} alerts
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---- RP sign-in — coverage today + stale open sessions ----------------------
function RpSignInCard({ pharmacy, token, viewToken, search, onRowDetail }) {
  const [sessions, setSessions] = useState(null); // recent sessions (drill)
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null);
    const p = viewToken
      ? apiGetPublic(viewToken, "rp")
      : apiGet(`single-extras/rp?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
    p.then(d => { if (!cancelled) { setData(d); setLoading(false); } })
     .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [pharmacy.pharmacyId, token, viewToken]);

  // Shapes (scoped server-side to this one pharmacy):
  //   coverage = { engaged, covered, uncovered, engagedWindowDays, offenders[] }
  //   stale    = [ { pharmacyName, offenders[] } ]  (fleetLive failing list)
  const cov = data?.coverage || null;
  const inUse = (cov?.engaged || 0) > 0;
  const signedInNow = (cov?.covered || 0) > 0;
  const offender = (cov?.offenders || [])[0] || null;
  const staleEntry = Array.isArray(data?.stale) ? (data.stale[0] || null) : null;
  const staleCount = staleEntry?.offenders?.length || 0;

  const labelMatch = search && "rp sign-in responsible pharmacist".includes(search);
  if (search && !labelMatch && !loading && !error &&
      !rowMatches({ signedInNow: signedInNow ? "signed in" : "not signed in",
                    minutesUncovered: offender?.minutesUncovered, staleCount }, search)) return null;

  const toggleSessions = async () => {
    if (sessions) { setSessions(null); return; }
    setSessionsLoading(true);
    try {
      const d = viewToken
        ? await apiGetPublic(viewToken, "rp-sessions")
        : await apiGet(`single-extras/rp-sessions?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
      setSessions(d.sessions || []);
    } catch (e) { window.alert("Couldn't load sessions: " + (e.message || e)); }
    finally { setSessionsLoading(false); }
  };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={S.cardLabel}>RP sign-in</div>
        {!loading && !error && (
          <button style={S.linkBtn} onClick={toggleSessions} disabled={sessionsLoading}>
            {sessionsLoading ? "Loading…" : sessions ? "Hide sessions" : "Recent sessions →"}
          </button>
        )}
      </div>
      {loading ? <div style={S.muted}>Loading…</div>
       : error ? <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn’t load: {error}</div>
       : !inUse && !sessions ? (
        <div style={S.muted}>
          No RP sign-in activity in the last {cov?.engagedWindowDays || 90} days.
          {data?.lastSession && (
            <span> Last session: {data.lastSession.pharmacistName || "RP"} on{" "}
              {data.lastSession.signInTime ? new Date(data.lastSession.signInTime).toLocaleDateString("en-GB") : "—"}.
            </span>
          )}
        </div>
       )
       : (
        <div style={S.statRow}>
          <div style={S.stat}>
            <div style={{ fontSize: 11, color: "#6b7280" }}>RP signed in now</div>
            <div style={{ fontSize: 16, fontWeight: 700,
                          color: signedInNow ? "#166534" : "#991b1b" }}>
              {signedInNow ? "Yes" : "No"}
            </div>
          </div>
          {!signedInNow && offender?.lastSignOutAt && (
            <Stat label="Last RP signed out" value={fmtDateTime(offender.lastSignOutAt)} />
          )}
          {!signedInNow && offender?.minutesUncovered != null && (
            <Stat label="Minutes uncovered" value={offender.minutesUncovered} highlight />
          )}
          <Stat label="Stale open sessions" value={staleCount} highlight={staleCount > 0} />
        </div>
      )}
      {sessions && (
        sessions.length === 0 ? <div style={S.muted}>No sessions recorded.</div> : (
          <table style={{ ...S.table, marginTop: 8 }}>
            <thead><tr>
              <th style={S.th}>Pharmacist</th><th style={S.th}>Signed in</th>
              <th style={S.th}>Signed out</th><th style={S.th}>CD keys</th>
            </tr></thead>
            <tbody>
              {(search ? filterRows(sessions, search) : sessions).map((x, i) => (
                <tr key={i} style={{ background: x.open ? "#fef9c3" : "transparent", cursor: "pointer" }}
                    onClick={() => onRowDetail && onRowDetail({
                      title: "RP session",
                      fields: [
                        ["Pharmacist", x.pharmacistName],
                        ["Signed in", fmtDateTime(x.signInTime)],
                        ["Signed out", x.open ? "Still open" : fmtDateTime(x.signOutTime)],
                        ["CD keys returned to safe", x.cdKeysReturnedToSafe == null ? null : (x.cdKeysReturnedToSafe ? "Yes" : "No")]
                      ]
                    })}>
                  <td style={S.td}>{x.pharmacistName || "—"}</td>
                  <td style={S.td}>{fmtDateTime(x.signInTime)}</td>
                  <td style={S.td}>{x.open ? "Still open" : fmtDateTime(x.signOutTime)}</td>
                  <td style={S.td}>{x.cdKeysReturnedToSafe == null ? "—" : (x.cdKeysReturnedToSafe ? "Returned" : "Handed over")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

// ---- Staff appraisals — anniversary-model status for this branch ------------
// Reuses POST /api/appraisals/status (org-scoped) and picks this pharmacy's
// row. That endpoint keys branches by the RAW pharmacies.name, which the
// single-pharmacy list exposes as lookupName — matched on that first.
// Authed only: the endpoint needs an org token and the overdue list carries
// staff names, so it is deliberately excluded from public viewing links.
function StaffAppraisalsSingleCard({ pharmacy, token, search, onRowDetail }) {
  const [row, setRow] = useState(null);
  const [names, setNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setRow(null); setNames([]);
    fetch(`${API_BASE}/api/appraisals/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({})
    })
      .then(r => r.json())
      .then(j => {
        if (cancelled) return;
        const match = (r) =>
          r.pharmacyName === pharmacy.lookupName || r.pharmacyName === pharmacy.pharmacyName;
        setRow((j.perPharmacy || []).find(match) || null);
        setNames(((j.outstanding || []).find(match) || {}).overdueNames || []);
        setLoading(false);
      })
      .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [token, pharmacy.pharmacyName, pharmacy.lookupName]);

  const labelMatch = search && "staff appraisals".includes(search);
  if (search && !labelMatch && !loading && !error &&
      !rowMatches({ ...(row || {}), names }, search)) return null;

  return (
    <div style={S.card}>
      <div style={S.cardLabel}>Staff appraisals</div>
      {loading ? <div style={S.muted}>Loading…</div>
       : error ? <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn't load: {error}</div>
       : !row ? <div style={S.muted}>No staff on record for this pharmacy.</div>
       : (
        <>
          <div style={{ ...S.statRow, cursor: onRowDetail ? "pointer" : "default" }}
               title="Click for the appraisal breakdown"
               onClick={() => onRowDetail && onRowDetail({
                 title: `Staff appraisals — ${pharmacy.pharmacyName}`,
                 fields: [
                   ["Staff on record", row.staffCount],
                   ["Up to date", row.ok],
                   ["Due soon (within 30 days of anniversary)", row.due],
                   ["Overdue (>12 months since last appraisal)", row.overdue],
                   ["Overdue staff", names.length ? names.join(", ") : (row.overdue > 0 ? "(names not returned)" : "None")]
                 ]
               })}>
            <Stat label="Staff" value={row.staffCount} />
            <Stat label="Up to date" value={row.ok} />
            <Stat label="Due soon" value={row.due} highlight={row.due > 0 && row.overdue === 0} />
            <Stat label="Overdue" value={row.overdue} highlight={row.overdue > 0} />
          </div>
          {names.length > 0 && (
            <div style={{ fontSize: 12, color: "#991b1b", marginTop: 4 }}>
              Overdue: {names.join(", ")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Near miss — this branch's recent incidents + monthly counts ------------
// Reads the tenant's raw near_misses via single-extras/nearmiss (or the
// public-view route on a viewing link), so it works even where the monthly
// summary engine hasn't run — and gives real rows to drill into.
function NearMissSingleCard({ pharmacy, token, viewToken, search, onRowDetail }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false); // tabbed dialog

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(""); setData(null);
    const p = viewToken
      ? apiGetPublic(viewToken, "nearmiss")
      : apiGet(`single-extras/nearmiss?pharmacyId=${encodeURIComponent(pharmacy.pharmacyId)}`, token);
    p.then(d => { if (!cancelled) { setData(d); setLoading(false); } })
     .catch(e => { if (!cancelled) { setError(String(e.message || e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [pharmacy.pharmacyId, token, viewToken]);

  const recent = data?.recent || [];
  const labelMatch = search && "near miss incidents".includes(search);
  const filtered = (search && !labelMatch) ? filterRows(recent, search) : recent;
  if (search && !labelMatch && !loading && !error && filtered.length === 0) return null;

  const shown = showAll ? filtered : filtered.slice(0, 8);

  // Detail popup: show every field the branch actually recorded on the doc.
  const openIncident = (r) => {
    if (!onRowDetail) return;
    const skip = new Set(["timestamp", "createdAt", "updatedAt"]);
    const extra = Object.entries(r.detail || {})
      .filter(([k, v]) => !skip.has(k) && v != null && typeof v !== "object")
      .map(([k, v]) => [k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()), String(v)]);
    onRowDetail({
      title: "Near miss",
      fields: [["Date", fmtDateTime(r.date)], ["Error type", r.errorTypeId],
               ["Description", r.description], ...extra]
    });
  };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ ...S.cardLabel, cursor: viewToken ? "default" : "pointer" }}
             title={viewToken ? undefined : "Open the full near-miss analysis for this pharmacy"}
             onClick={viewToken ? undefined : () => setAnalysisOpen(true)}>
          Near miss
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!loading && !error && data?.inUse && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              {data.thisMonth} this month · {data.lastMonth} last month · {data.total} all time
            </span>
          )}
          {!viewToken && (
            <button style={S.linkBtn} onClick={() => setAnalysisOpen(true)}>Open analysis →</button>
          )}
        </div>
      </div>
      {analysisOpen && !viewToken && (
        <NearMissPharmacyDialog
          pharmacyName={pharmacy.lookupName || pharmacy.pharmacyName}
          pharmacyId={pharmacy.pharmacyId}
          onClose={() => setAnalysisOpen(false)}
        />
      )}
      {loading ? <div style={S.muted}>Loading…</div>
       : error ? <div style={{ color: "#7f1d1d", fontSize: 13 }}>Couldn't load: {error}</div>
       : !data?.inUse ? <div style={S.muted}>No near misses recorded at this pharmacy.</div>
       : (
        <>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Date</th><th style={S.th}>Error type</th><th style={S.th}>Description</th>
            </tr></thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={i} style={{ cursor: "pointer" }} onClick={() => openIncident(r)}>
                  <td style={S.td}>{fmtDate(r.date)}</td>
                  <td style={S.td}>{r.errorTypeId || "—"}</td>
                  <td style={S.td}>{r.description
                    ? (String(r.description).length > 80 ? String(r.description).slice(0, 80) + "…" : r.description)
                    : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!showAll && filtered.length > shown.length && (
            <button style={{ ...S.linkBtn, marginTop: 8 }} onClick={() => setShowAll(true)}>
              Show all {filtered.length} recent incidents
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ---- Staff training — placeholder, mirroring the Head Office card ----------
// ** NOT LIVE ** There is no training-assignments backend yet
// (staff_training_defs.js is the requirements dictionary only). This card
// keeps panel parity with Head Office and will light up when the training
// module ships — deliberately NOT showing mock figures here.
function StaffTrainingSingleCard({ search }) {
  const labelMatch = !search || "staff training".includes(search);
  if (search && !labelMatch) return null;
  return (
    <div style={{ ...S.card, background: "#fafafa" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={S.cardLabel}>Staff training</div>
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
                       padding: "1px 6px", borderRadius: 4, background: "#fef3c7", color: "#92400e",
                       border: "1px solid #fde68a" }}>Not live yet</span>
      </div>
      <div style={{ fontSize: 12, color: "#6b7280" }}>
        Training compliance per staff member (induction items, refreshers, GPhC /
        DSPT / safeguarding requirements) will appear here once the training
        module is wired up — the requirements dictionary already exists on the
        backend.
      </div>
    </div>
  );
}